import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTpStreamToken } from "hook/useTpStreamToken";
import * as questActions from "store/actions/index";
import { CURRENT_DATE_TIME } from "variables/general";

const TpStreamVideoPlayer = ({
  tps_asset_id,
  className = "",
  title = "TPStream Player",
  currentContentDetails,
  contentId,
  activitySummary,
  autoPlay = true,
  muted = true,
}) => {
  const dispatch = useDispatch();

  const userInfo = useSelector((state) => state.user.userinfo);
  const quest = useSelector((state) => state.quest.current_quest);
  const content = useSelector((state) => state.content.current_content);
  const accessDateTime = useSelector((state) => state.content.accessDateTime);

  const iframeElementRef = useRef(null);
  const playerRef = useRef(null);

  const lastPositionRef = useRef(0);
  const durationRef = useRef(0);
  const hasPostedEndRef = useRef(false);
  const lastSavedSecondRef = useRef(0);

  const hasId = useMemo(() => {
    return Boolean(tps_asset_id && String(tps_asset_id).trim());
  }, [tps_asset_id]);

  const { data, isLoading, error, refetch } = useTpStreamToken(tps_asset_id);

  const resolveUrl = (payload) => {
    if (!payload) return undefined;

    return (
      payload.playback_url ||
      payload.embedUrl ||
      payload.iframe_url ||
      payload.signedUrl ||
      payload.url
    );
  };

  const withAutoplayParams = (url, shouldAutoPlay, shouldMute) => {
    if (!url) return undefined;

    try {
      const parsed = new URL(url, window.location.origin);

      if (shouldAutoPlay) {
        parsed.searchParams.set("autoplay", "true");
        parsed.searchParams.set("auto_play", "true");
        parsed.searchParams.set("autostart", "true");
      }

      if (shouldMute) {
        parsed.searchParams.set("muted", "true");
        parsed.searchParams.set("mute", "true");
      }

      return parsed.toString();
    } catch (e) {
      return url;
    }
  };

  const [stableSrc, setStableSrc] = useState(undefined);
  const prevSrcRef = useRef(undefined);
  const [iframeLoaded, setIframeLoaded] = useState(false);

  const updateContentAccessDuration = (seconds) => {
    dispatch(questActions.updateContentAccessDuration(seconds));
  };

  const postContentAccess = (overrideDuration) => {
    if (!content || !content.contentId) return;
    if (!userInfo || !quest) return;

    const finalDuration = Math.max(
      0,
      Number(
        typeof overrideDuration === "number"
          ? overrideDuration
          : content.accessDuration || lastPositionRef.current || 0,
      ),
    );

    if (finalDuration <= 1) return;

    const BODY_DATA = {
      missionActivityId: "",
      userId: userInfo.userId,
      locationId: userInfo.locationId,
      groupId: userInfo.groupId,
      questId: quest.questId,
      contentId: content.contentId,
      contentTypeCode: content.contentTypeCode,
      accessDate: "" + (accessDateTime || CURRENT_DATE_TIME()),
      accessDuration: "" + finalDuration,
      missionActivitySummaryId: "",
    };

    dispatch(questActions.postContentAccess(BODY_DATA, content));
  };

  useEffect(() => {
    const raw = resolveUrl(data);
    const next = withAutoplayParams(raw, autoPlay, muted);

    if (next && next !== prevSrcRef.current) {
      setIframeLoaded(false);
      setStableSrc(next);
      prevSrcRef.current = next;
      hasPostedEndRef.current = false;
      lastPositionRef.current = 0;
      durationRef.current = 0;
      lastSavedSecondRef.current = 0;
    }
  }, [data, autoPlay, muted]);

  useEffect(() => {
    let isMounted = true;

    const loadPlayerScript = () => {
      return new Promise((resolve, reject) => {
        if (window.Testpress && window.Testpress.Player) {
          resolve();
          return;
        }

        const existingScript = document.querySelector(
          'script[src="https://static.testpress.in/static/js/player.js"]',
        );

        if (existingScript) {
          existingScript.addEventListener("load", resolve, { once: true });
          existingScript.addEventListener(
            "error",
            function () {
              reject(new Error("Failed to load Testpress player script"));
            },
            { once: true },
          );
          return;
        }

        const script = document.createElement("script");
        script.src = "https://static.testpress.in/static/js/player.js";
        script.async = true;
        script.onload = resolve;
        script.onerror = function () {
          reject(new Error("Failed to load Testpress player script"));
        };
        document.body.appendChild(script);
      });
    };

    const initializePlayer = async () => {
      if (!stableSrc || !iframeElementRef.current) return;

      try {
        await loadPlayerScript();

        if (!isMounted || !iframeElementRef.current) return;
        if (!window.Testpress || !window.Testpress.Player) return;

        const player = new window.Testpress.Player(iframeElementRef.current);
        playerRef.current = player;

        player.on("play", function () {
          if (typeof CURRENT_DATE_TIME === "function") {
            // same like old player onReady/onPlay flow if needed
          }

          player.getDuration().then(function (duration) {
            durationRef.current = Number(duration || 0);
          });
        });

        player.on("timeupdate", function () {
          player.getCurrentTime().then(function (seconds) {
            const currentSeconds = Number(seconds || 0);
            lastPositionRef.current = currentSeconds;

            updateContentAccessDuration(currentSeconds);

            if (currentContentDetails) {
              currentContentDetails.current = {
                missionActivitySummaryId:
                  activitySummary && activitySummary.MissionActivitySummaryId,
                contentId: contentId,
                position: currentSeconds,
              };
            }

            // optional autosave every 15 sec watched
            if (currentSeconds - lastSavedSecondRef.current >= 15) {
              lastSavedSecondRef.current = currentSeconds;
              postContentAccess(currentSeconds);
            }
          });

          player.getDuration().then(function (duration) {
            durationRef.current = Number(duration || 0);
          });
        });

        player.on("ended", function () {
          if (hasPostedEndRef.current) return;
          hasPostedEndRef.current = true;

          const watched = lastPositionRef.current || durationRef.current || 0;
          postContentAccess(watched);
        });

        if (autoPlay) {
          try {
            if (muted && typeof player.setMuted === "function") {
              await player.setMuted(true);
            }
          } catch (e) {}

          try {
            await player.play();
          } catch (err) {
            console.warn("Autoplay blocked by browser or player policy:", err);
          }
        }
      } catch (err) {
        console.error("TPStream player initialization failed:", err);
      }
    };

    initializePlayer();

    return () => {
      isMounted = false;

      const watched = lastPositionRef.current || 0;
      if (watched > 1 && !hasPostedEndRef.current) {
        postContentAccess(watched);
      }

      playerRef.current = null;
    };
  }, [
    stableSrc,
    tps_asset_id,
    autoPlay,
    muted,
    currentContentDetails,
    activitySummary,
    contentId,
  ]);

  const errorMsg =
    error instanceof Error ? error.message : error ? String(error) : "";

  if (!hasId) return null;

  const showInitialLoading = !stableSrc && isLoading;

  return (
    <div className={className} style={{ position: "relative", width: "100%" }}>
      <div
        style={{
          position: "relative",
          width: "80%",
          paddingTop: "43.25%",
          margin: "0 auto",
        }}
      >
        {!showInitialLoading && !iframeLoaded && stableSrc ? (
          <div
            style={{
              position: "absolute",
              inset: 0,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              background:
                "linear-gradient(135deg, rgb(23,23,23), rgb(38,38,38), rgb(23,23,23))",
              color: "#fff",
              borderRadius: 12,
            }}
          >
            <div
              style={{
                height: 40,
                width: 40,
                borderRadius: "50%",
                border: "2px solid rgba(255,255,255,0.25)",
                borderTop: "2px solid white",
                animation: "tpstream-spin 0.8s linear infinite",
              }}
            />
            <span style={{ marginTop: 12, fontSize: 14, opacity: 0.9 }}>
              Loading video...
            </span>
          </div>
        ) : null}

        {stableSrc ? (
          <iframe
            ref={iframeElementRef}
            title={title}
            src={stableSrc}
            style={{
              position: "absolute",
              left: 0,
              top: 0,
              width: "100%",
              height: "100%",
              border: 0,
              borderRadius: 12,
              opacity: iframeLoaded ? 1 : 0,
              transition: "opacity 0.2s ease",
            }}
            onLoad={() => setIframeLoaded(true)}
            allow="autoplay; accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
            sandbox="allow-same-origin allow-scripts allow-forms allow-presentation allow-popups"
            referrerPolicy="no-referrer-when-downgrade"
          />
        ) : null}

        {!stableSrc && !showInitialLoading && errorMsg ? (
          <div
            style={{
              position: "absolute",
              inset: 0,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: 12,
              borderRadius: 12,
              border: "1px solid #fecaca",
              background: "#fef2f2",
              padding: 16,
              textAlign: "center",
            }}
          >
            <div style={{ fontSize: 14, color: "#b91c1c" }}>{errorMsg}</div>
            <button
              type="button"
              onClick={refetch}
              style={{
                background: "#dc2626",
                color: "#fff",
                border: 0,
                borderRadius: 6,
                padding: "8px 14px",
                cursor: "pointer",
              }}
            >
              Retry
            </button>
          </div>
        ) : null}
      </div>

      {title && (
        <div
          style={{
            marginTop: 10,
            fontSize: 15,
            fontWeight: 600,
            color: "#1f2937",
            textAlign: "center",
          }}
        >
          {title}
        </div>
      )}

      <style>
        {`
          @keyframes tpstream-spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );
};

export default TpStreamVideoPlayer;
